<script lang="ts">
    import { untrack } from 'svelte';
    import ApmBoundary from '@shared/components/apm-boundary/ApmBoundary.svelte';
    import { log } from '@shared/log.browser.ts';
    import type { OnColorChangePayload } from '@shared/types/types.ts';
    import { isSimilarProduct, isSponsoredProduct } from '@shared/types/types.ts';
    import type { ImageLoadingOptions } from '@tile/components/product-image/types.ts';
    import TileItemObserver from '@tilelist/components/product-tile-item/observation/TileItemObserver.svelte';
    import { tileObserver } from '@tilelist/components/product-tile-item/observation/TileObserver.browser.ts';
    import PureProductTile from '@tilelist/components/product-tile-item/PureProductTile.svelte';
    import Placeholder from '@tilelist/components/tile-items/Placeholder.svelte';
    import type { BracketLayout } from '@tilelist/domain/composition/BracketLayout.ts';
    import type { ProductItem } from '@tilelist/domain/composition/TileListitem.ts';
    import TileLayoutDispatcher from '@tilelist/layouts/dispatchers/tile-layout-dispatcher/TileLayoutDispatcher.svelte';

    type Props = {
        productItem: ProductItem;
        rule: string;
        layout: BracketLayout;
        cacheRestored: boolean;
        loadVariation: (variationId: string) => Promise<void>;
        loadColorVariation: (payload: OnColorChangePayload) => Promise<void>;
        onSeen: () => Promise<void>;
        onTileClick: (event: Event) => void;
        showRadioEquipmentRegulation: boolean;
        showOttoUp?: boolean;
        showTopInfos: boolean;
        showInstallments: boolean;
        showMultipleSizes: boolean;
    };

    let {
        productItem,
        onTileClick,
        cacheRestored,
        rule,
        layout,
        loadVariation,
        loadColorVariation,
        onSeen,
        showRadioEquipmentRegulation,
        showOttoUp = true,
        showTopInfos,
        showInstallments,
        showMultipleSizes,
    }: Props = $props();

    const variation = $derived.by(() => {
        if (!productItem.currentVariationId) return undefined;

        const currentVariation = productItem.variations.find((v) => v.variationId === productItem.currentVariationId);

        if (!currentVariation) return;

        return {
            ...currentVariation,
            radioEquipmentRegulation: showRadioEquipmentRegulation
                ? currentVariation.radioEquipmentRegulation
                : undefined,
            ottoUp: showOttoUp ? currentVariation.ottoUp : undefined,
        };
    });

    let seen = $state(false);
    const tileState = $derived({
        loaded: productItem.variations.length > 0 && !!productItem.currentVariationId,
        seen,
    });

    const colors = $derived(productItem.colors);

    /**
 *
 *
 *
 *
 */
    const dataOrigin = $derived.by(() => {
        if (isSponsoredProduct(productItem.product)) return 'sponsored';
        if (isSimilarProduct(productItem.product)) return 'semantic';
        return undefined;
    });

    async function handleLoadBestVariation() {
        const bestVariationId = productItem.product.bestVariationId;
        if (!bestVariationId) return;
        await loadVariation(bestVariationId);
    }

    async function handleSeen() {
        try {
            await onSeen();
            seen = true;
        } catch (error) {
            log.warn('Failed to track tile visibility:', error);
        }
    }

    function calculateImageLoadingOptions(localListPosition: number | undefined): ImageLoadingOptions {
        if (localListPosition && localListPosition < 8)
            return {
                fetchpriority: 'high',
                loading: 'eager',
            };

        return {
            fetchpriority: 'auto',
            loading: 'lazy',
        };
    }

    const productArticleElem = $state<HTMLElement | null>(null);

    /**
 *
 */
    function getCoreProperties() {
        return {
            element: productArticleElem,
            sponsored: isSponsoredProduct(productItem.product),
            ...(isSponsoredProduct(productItem.product)
                ? {
                      advertiserLegalName: productItem.product.advertiserLegalName,
                      funderLegalName: productItem.product.funderLegalName,
                  }
                : {}),
            imageLoadingOptions: calculateImageLoadingOptions(productItem.localListPosition),
            colors,
            rule,
            onColorChange: loadColorVariation,
            onRedirectToPdp: onTileClick,
            bestVariationBenefit: productItem.product.bestVariationBenefit,
            showOttoUp,
        };
    }

    let itemLoadingAttachment = $state();
    let itemTrackingAttachment = $state();

    /**
 *
 *
 */
    let lastContextRule = $state<string>(untrack(() => rule));
    $effect(() => {
        if (rule !== lastContextRule) {
            lastContextRule = rule;
            seen = false;
        }
    });
</script>

{#snippet tileLayoutSnippet(v: NonNullable<typeof variation>)}
    <TileLayoutDispatcher
        {...getCoreProperties()}
        tileLayout={layout.tileLayout}
        variation={v}
        aspectRatio={layout.imageAspectRatio}
        {showTopInfos}
        {showInstallments}
        {showMultipleSizes}
    />
{/snippet}

<ApmBoundary scope="product-tile-item">
    <!-- Loading observer: triggers when tile is near viewport -->
    <TileItemObserver
        action={handleLoadBestVariation}
        successful={tileState.loaded || productItem.variationLoadFailed === true}
        {cacheRestored}
        observe={tileObserver.observeBeforeEnter.bind(tileObserver)}
        onAttachment={(a) => (itemLoadingAttachment = a)}
    />

    <!-- Tracking observer: triggers when tile is fully visible -->
    <TileItemObserver
        action={handleSeen}
        successful={tileState.seen}
        cacheRestored={cacheRestored && tileState.loaded}
        observe={tileObserver.observeOnEnter.bind(tileObserver)}
        onAttachment={(a) => (itemTrackingAttachment = a)}
    />

    <PureProductTile
        {@attach itemLoadingAttachment}
        {@attach itemTrackingAttachment}
        articleNumber={productItem.product.articleNumber}
        productId={productItem.product.id}
        variationId={productItem.product.bestVariationId}
        listPosition={productItem.actualListPosition}
        localListPosition={productItem.localListPosition}
        origin={dataOrigin}
    >
        {#if productItem.product}
            {#if variation}
                {@render tileLayoutSnippet(variation)}
            {:else}
                <Placeholder title={productItem.product.name} href={productItem.product.variationPath} />
            {/if}
        {:else}
            <p>This should never be reached</p>
        {/if}
    </PureProductTile>
</ApmBoundary>
